// @flow

import ScreenSizes from '../constants/ScreenSizes';
import KeyCodes from '../constants/KeyCodes';
import MeasuringUnits from '../constants/MeasuringUnits';
import Collection from './Collection';
import Url from './Url/Url';
import Environments from '../constants/Environments';

/**
 * Check if the screen is a small screen
 *
 * @returns {boolean}
 */
export function isSmallScreen(): boolean {
  return window.outerWidth < ScreenSizes.MEDIUM;
}

/**
 * Check if the screen is large or up
 *
 * @returns {boolean}
 */
export function isLargeScreen(): boolean {
  return window.outerWidth >= ScreenSizes.LARGE;
}

/**
 * Check if the screen is an extra large screen
 *
 * @returns {boolean}
 */
export function isExtraLargeScreen(): boolean {
  return window.outerWidth >= ScreenSizes.EXTRA_LARGE;
}

/**
 * Convert number to price
 *
 * @param number
 * @returns {string}
 */
export function price(number: number): string {
  return new Intl.NumberFormat(window.locale, {
    style: 'currency',
    currency: window.currency
  }).format(number);
}

/**
 * Get the current url
 *
 * @returns {string}
 */
export function currentUrl(): string {
  return window.store.url.current();
}

/**
 * Generate a url
 *
 * @param path - {string}
 * @returns {string}
 */
export function url(path: string): string {
  const location: Object = window.location;
  return location.protocol + '//' + location.host + path;
}

/**
 * Get the config value of a given string
 */
export function config(file: string, key: string): string {
  return require(`../../../yarn-config/${file}`)[key];
}

/**
 * Get the outerHeight (including margin) of a element
 *
 * @param element
 * @returns {*}
 */
export function outerHeight(element: any): number {
  const style: any = element.currentStyle || window.getComputedStyle(element);
  const margin: number = parseInt(style.marginTop || 0, 10) + parseInt(style.marginBottom || 0, 10);
  return element.offsetHeight + margin;
}

/**
 * Get the outerWidth (including margin) of a element
 *
 * @param element
 * @returns {*}
 */
export function outerWidth(element: any): number {
  const style: any = element.currentStyle || window.getComputedStyle(element);
  const margin: number = parseInt(style.marginLeft || 0, 10) + parseInt(style.marginRight || 0, 10);
  return element.offsetWidth + margin;
}

/**
 * Check if the element is overflown
 *
 * @param element
 * @returns {boolean}
 */
export function isOverflown(element: HTMLElement): boolean {
  // number 2 was chosen because of the difference between chrome and firefox
  return element.scrollHeight - element.clientHeight > 2;
}

/**
 * Convert a string from camelCase to kebab-case
 *
 * @param string
 * @returns {string}
 */
export function camelToKebab(string: string): string {
  return string.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Convert a string to camelCase
 */
export function toCamel(string: string): string {
  return string.replace(/([-_][a-z])/ig, (segment: string) => {
    return segment.toUpperCase()
      .replace('-', '')
      .replace('_', '');
  });
}

/**
 * Capitalize the first letter of the given string
 */
export function uppercaseFirst(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Lowercase the first letter of the given string
 */
export function lowercaseFirst(string: string): string {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * Check if the key is the enter key
 *
 * @param e
 * @returns {boolean}
 */
export function isEnterKey(e: any): boolean {
  return e.key === 'Enter' || e.keyCode === KeyCodes.ENTER;
}

/**
 * Removes all child elements of a element from the DOM
 * Proven to be faster than innerHTML = ''
 *
 * @param parent
 */
export function clearChildElements(parent: HTMLElement): void {
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }
}

/**
 * Make the distance property a bit more readable
 *
 * @param distance
 * @param actual
 * @param expected
 * @returns {string | null}
 */
export function distance(
  distance: number,
  actual: string = MeasuringUnits.METERS,
  expected: string = MeasuringUnits.KILOMETERS
): ?string {
  if (actual === MeasuringUnits.METERS && expected === MeasuringUnits.KILOMETERS) {
    return `${Math.round((distance / 1000) * 10) / 10} ${MeasuringUnits.KILOMETERS}`
      .replace('.', ',');
  }

  return null;
}

/**
 * Convert a string to an array by a given delimiter
 *
 * @param string
 * @param delimiter
 * @returns {Array<string>}
 */
export function stringToArray(string: string, delimiter: string = ','): Array<string> {
  return string.split(' ').join('').split(delimiter);
}

/**
 * Remove all whitespace from a string
 *
 * @param string
 * @returns {string}
 */
export function stringRemoveWhitespace(string: string): string {
  return string.replace(/\s+/g, '');
}

/**
 * Check if 2 objects are equal
 *
 * @param pA
 * @param pB
 * @returns {boolean}
 */
export function isEqual(pA: Object, pB: Object): boolean {
  const keysA: any = Object.keys(pA).sort();
  const keysB: any = Object.keys(pB).sort();
  const objectA: Object = {};
  const objectB: Object = {};
  keysA.forEach(function (key: any) {
    objectA[key] = pA[key];
  });
  keysB.forEach(function (key: any) {
    objectB[key] = pB[key];
  });
  return JSON.stringify(objectA) === JSON.stringify(objectB);
}

/**
 * Helper to create a new collection
 *
 * @param items
 */
export function collect<T>(items: Array<T>): Collection<T> {
  return new Collection(items);
}

/**
 * Check if a given object has the given value
 *
 * @param object
 * @param value
 */
export function objectHasValue(object: Object, value: string): boolean {
  return Object.values(object).indexOf(value) > -1;
}

/**
 * returns a ajax parameter to add after a async request
 */
export function ajaxParameter(): string {
  let ajaxParameter;
  if (new Url().hasParameters()) {
    ajaxParameter = '&ajax=1';
  } else {
    ajaxParameter = '?ajax=1';
  }
  return ajaxParameter;
}

/**
 * Check if the given HTML element is visible
 *
 * Note: Does not work for fixed elements. If needed, adjust this implementation
 */
export function isVisible(element: HTMLElement): boolean {
  return !!element.offsetParent;
}

/**
 * Get the percentage of the visibility of the given element
 */
export function percentageVisible(element: HTMLElement): number {
  const viewport: {
    top: number,
    bottom: number
  } = {
    top: window.pageYOffset,
    bottom: window.pageYOffset + window.innerHeight
  };

  const elementBoundingRect = element.getBoundingClientRect();
  const elementPos: {
    top: number,
    bottom: number
  } = {
    top: elementBoundingRect.y + window.pageYOffset,
    bottom: elementBoundingRect.y + elementBoundingRect.height + window.pageYOffset
  };

  if (viewport.top > elementPos.bottom || viewport.bottom < elementPos.top) {
    return 0;
  }

  // Element is fully within viewport
  if (viewport.top < elementPos.top && viewport.bottom > elementPos.bottom) {
    return 100;
  }

  // Element is bigger than the viewport
  if (elementPos.top < viewport.top && elementPos.bottom > viewport.bottom) {
    return 100;
  }

  const elementHeight = elementBoundingRect.height;
  let elementHeightInView = elementHeight;

  if (elementPos.top < viewport.top) {
    elementHeightInView = elementHeight - (window.pageYOffset - elementPos.top);
  }

  if (elementPos.bottom > viewport.bottom) {
    elementHeightInView = elementHeightInView - (elementPos.bottom - viewport.bottom);
  }

  const percentageInView = (elementHeightInView / window.innerHeight) * 100;

  return Math.round(percentageInView);
}

/**
 * Helper function to create abstract methods on classes
 */
export function abstract(): void {
  throw new Error('Abstract method');
}

/**
 * Escape Regex
 */
export function escapeRegExp(string: string) {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replace all occurrences in a string
 */
export function replaceAll(target: string, query: string, replace: string) {
  return target.replace(new RegExp(escapeRegExp(query), 'g'), replace);
}

/**
 * Add months to a date object
 */
export function addMonths(date: Date, months: number): Date {
  return new Date(date.setMonth(date.getMonth() + months));
}

/**
 * Check whether the given dates are the same day
 */
export function isSameDay(date: Date | number, comparison: Date | number): boolean {
  date = convertToDate(date);
  comparison = convertToDate(comparison);

  return date.getFullYear() === comparison.getFullYear() &&
    date.getMonth() === comparison.getMonth() &&
    date.getDate() === comparison.getDate();
}

/**
 * Return the difference in months
 *
 * For now. It only works for the future, adjust when needed
 */
export function differenceInMonths(date: Date | number, comparison: Date | number): number {
  date = convertToDate(date);
  comparison = convertToDate(comparison);

  let months;
  months = (date.getFullYear() - comparison.getFullYear()) * 12;
  months -= comparison.getMonth();
  months += date.getMonth();

  return months <= 0 ? 0 : months;
}

/**
 * Convert the given date (can be a number) to a date object
 */
export function convertToDate(date: Date | number): Date {
  if (typeof date === 'number') {
    return new Date(date);
  }

  return date;
}

/**
 * Give an amount and it will be converted to a translation to indicate the period of a subscription
 *
 * @param amount
 * @param frequency
 */
export function subscriptionPeriodTranslation(amount: number, frequency: number): string {
  if (amount === 0) {
    return window.store.getTranslation('subscription.choose.frequency');
  }

  if (amount === 1) {
    switch (true) {
      case frequency === 1:
        return window.store.getTranslation('subscription.frequency.week').replace(':amount', 1);
      case frequency === 2:
        return window.store.getTranslation('subscription.frequency.month').replace(':amount', 1);
    }
  }

  if (amount > 1) {
    switch (true) {
      case frequency === 1:
        return window.store.getTranslation('subscription.frequency.weeks').replace(':amount', amount);
      case frequency === 2:
        return window.store.getTranslation('subscription.frequency.months').replace(':amount', amount);
    }
  }
}

export function sortObjectAlphabetically(object: Object, sortProperty: string): Object {
  return object.sort(function (object1: Object, object2: Object) {
    return (object1[sortProperty].toUpperCase() < object2[sortProperty].toUpperCase()) ? -1 : (object1[sortProperty].toUpperCase() > object2[sortProperty].toUpperCase()) ? 1 : 0;
  });
}

export function decodeURIComponentSafe(string: string) {
  if (!string) return string;
  return decodeURIComponent(string.replace(/%(?![0-9][0-9a-fA-F]+)/g, '%25'));
}

export function mobileCheck() {
  let check = false;
  (function (a: string) {
    if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substr(0, 4))) check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}
